import { useQuery } from '@apollo/client';
import { IntlProvider } from 'react-intl';
import styled from 'styled-components';

import { ProductType } from '@xing-com/crate-common-graphql-types';
import { useLocation, useParams } from '@xing-com/crate-core-router';
import { GetCampaignPreviewDocument } from '@xing-com/crate-upsell-graphql';
import { type Xinglet } from '@xing-com/crate-xinglet';
import { LogoXINGButterflyDuo } from '@xing-com/illustrations';
import { Hero } from '@xing-com/typography';

import useTranslations from '../hooks/use-translations';

import { CampaignPage } from './campaign-page';

export const CampaignPagePreview: React.FC = () => {
  const { campaignIdentifier } = useParams();
  const searchParams = new URLSearchParams(useLocation().search);
  const language = searchParams.get('language') || 'de';
  const translations = useTranslations(language);

  const { loading, error, data } = useQuery(GetCampaignPreviewDocument, {
    variables: {
      identifier: campaignIdentifier,
      language,
    },
    skip: !campaignIdentifier || !language,
  });

  if (loading || translations === undefined) return null;

  if (!data?.results || error)
    return (
      <NoResultsWrapper>
        <StyledHero size="small" sizeWide="small">
          {error ? (
            <label>Error in network request: {error?.message}</label>
          ) : (
            <label>NO RESULTS FOUND</label>
          )}
        </StyledHero>
        <LogoXINGButterflyDuo width={50} />
      </NoResultsWrapper>
    );

  const product = data.results.contracts[0].featureSet.toLocaleLowerCase();
  if (product !== ProductType.Premium && product !== ProductType.Projobs) {
    return <h1>Product not found</h1>;
  }

  //eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
  const messages = (translations as { messages?: any })?.messages;

  return (
    <IntlProvider locale={language || 'de'} messages={messages}>
      <CampaignPage
        isPreview
        product={product}
        campaign={data.results}
        loading={loading}
        status={'SUCCESS'}
        premiumContactsLoading={false}
        premiumContacts={undefined}
      />
    </IntlProvider>
  );
};

const NoResultsWrapper = styled.div`
  height: 100vh;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  justify-content: center;
  padding: 0 20px;
  text-align: center;
  gap: 20px;
`;

const StyledHero = styled(Hero)`
  margin: 0;
`;

export default class PremiumOffers implements Xinglet {
  public getComponent(): React.FC {
    return CampaignPagePreview;
  }
}
